import React from 'react'

import { alpha, Stack, useTheme } from '@mui/material'

import { Button } from '@repo/ui'

export interface SegmentedChoiceOption<T extends string | number> {
  value: T
  label: string
  /** Leading glyph. Optional, but all-or-nothing across one control's options. */
  icon?: React.ReactNode
}

interface SegmentedChoiceProps<T extends string | number> {
  options: readonly SegmentedChoiceOption<T>[]
  value: T
  onChange: (value: T) => void
  disabled?: boolean
  /** Names the group for screen readers, e.g. "Color mode", "Interface scale". */
  ariaLabel: string
}

/**
 * Connected segmented control — a single-select row of joined buttons where the
 * shared borders collapse and only the outer corners round.
 *
 * Extracted from the Theme picker, which had lived inline in ProfilePage
 * as three hand-rolled buttons repeating ~20 lines of tint/border/zIndex `sx`
 * apiece. Text size needed the same control, and a second hand-roll is where the
 * two copies start to drift (working-conventions §36) — one gets a hover fix the
 * other does not, and two controls that must read as siblings stop matching.
 *
 * Selection styling follows the selectable-card rule (§34): a brand BORDER plus
 * a 10% tint means "chosen", a 6% tint alone means "considering". Hover never
 * takes the brand border, or hover and selected would be indistinguishable.
 *
 * The -1px left margin is what collapses the shared edges into a single line;
 * the raised zIndex on the selected (and hovered) button keeps its brand border
 * drawn on top of its neighbour's grey one rather than half-hidden under it.
 */
export const SegmentedChoice = <T extends string | number>({
  options,
  value,
  onChange,
  disabled = false,
  ariaLabel,
}: SegmentedChoiceProps<T>) => {
  const theme = useTheme()

  return (
    <Stack direction="row" spacing={0} role="radiogroup" aria-label={ariaLabel}>
      {options.map((option, index) => {
        const selected = option.value === value
        const isFirst = index === 0
        const isLast = index === options.length - 1

        return (
          <Button
            key={option.value}
            fullWidth
            disableElevation
            type="button"
            role="radio"
            aria-checked={selected}
            variant="secondary"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            startIcon={option.icon}
            sx={{
              textAlign: 'center',
              // Only the outer corners of the whole track are rounded.
              ...(isFirst && { borderTopRightRadius: 0, borderBottomRightRadius: 0 }),
              ...(isLast && { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }),
              ...(!isFirst && !isLast && { borderRadius: 0 }),
              ...(!isFirst && { marginLeft: '-1px' }),
              position: 'relative',
              zIndex: selected ? 1 : 0,
              ...(selected && {
                backgroundColor: alpha(theme.palette.brand[500], 0.1),
                borderColor: theme.palette.brand[500],
                color: theme.palette.brand[500],
              }),
              '&:hover': {
                backgroundColor: selected
                  ? alpha(theme.palette.brand[500], 0.1)
                  : alpha(theme.palette.brand[500], 0.06),
                color: theme.palette.brand[800],
                zIndex: 1,
              },
            }}
          >
            {option.label}
          </Button>
        )
      })}
    </Stack>
  )
}
